import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_PROJECTITEMTEMPLATES_LOAD,
    ACTION_PROJECTITEMTEMPLATES_LOAD_BY_ID,
    ACTION_PROJECTITEMTEMPLATES_LOAD_FAILURE,
    ACTION_PROJECTITEMTEMPLATES_LOAD_SUCCESS,

    ACTION_PROJECTITEMTEMPLATES_SAVE,
    ACTION_PROJECTITEMTEMPLATES_SAVE_SUCCESS,
    ACTION_PROJECTITEMTEMPLATES_SAVE_FAILURE,

    ACTION_PROJECTITEMTEMPLATES_CREATE,
    ACTION_PROJECTITEMTEMPLATES_CREATE_SUCCESS,
    ACTION_PROJECTITEMTEMPLATES_CREATE_FAILURE,

    ACTION_PROJECTITEMTEMPLATES_VALIDATE,
    ACTION_PROJECTITEMTEMPLATES_VALIDATE_SUCCESS,
    ACTION_PROJECTITEMTEMPLATES_VALIDATE_FAILURE,

    ACTION_PROJECTITEMTEMPLATES_CLEAR,
    ACTION_PROJECTITEMTEMPLATES_FILTER,

    ProjectItemTemplateAction
} from '../actions/projectItemTemplates';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";


export interface IProjectItemTemplateState {
    formData: Dtos.ProjectItemTemplate | Dtos.ProjectItemTemplate[];
    formDataGrouped: Dtos.ProjectItemTemplateCategory[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.ProjectItemTemplate[];
    formFilteredDataGrouped: Dtos.ProjectItemTemplateCategory[];
}

const initialState: IProjectItemTemplateState = {
    formData: undefined,
    formDataGrouped: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formFilteredDataGrouped: undefined,
};


function loading(state: IProjectItemTemplateState, _isLoading: boolean): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IProjectItemTemplateState,
    formData: Dtos.ProjectItemTemplate | Dtos.ProjectItemTemplate[],
    formDataGrouped: Dtos.ProjectItemTemplateCategory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        formData: {
            $set: formData
        },
        formDataGrouped: {
            $set: formDataGrouped
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IProjectItemTemplateState, formFilter: IFormFilter): IProjectItemTemplateState {

    const formData: Dtos.ProjectItemTemplate[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = formData ? formData.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    let formFilteredDataGrouped = [];
    if (state.formDataGrouped) {
        for (var i = 0; i < state.formDataGrouped.length; i++) {
            const filteredCategoryItems =
                state.formDataGrouped[i].projectItemTemplates.filter(p => (p.active == true || formFilter.showInActive) &&
                    (formFilter.search == "" ||
                        (p.name != undefined && p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1) ||
                        (p.description != undefined && p.description.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
                    ));
            if (filteredCategoryItems.length > 0) {
                formFilteredDataGrouped.push({
                    ...state.formDataGrouped[i],
                    projectItemTemplates: filteredCategoryItems
                });
            }
        }
    }

    const newState: IProjectItemTemplateState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formFilteredDataGrouped: {
            $set: formFilteredDataGrouped
        }
    });

    return newState;
}

function loadFailure(state: IProjectItemTemplateState, responseStatus: Dtos.ResponseStatus): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IProjectItemTemplateState, propertiesToClear?: string[]): IProjectItemTemplateState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IProjectItemTemplateState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IProjectItemTemplateState, _isSaving: boolean): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IProjectItemTemplateState,
    formData: Dtos.ProjectItemTemplate | Dtos.ProjectItemTemplate[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IProjectItemTemplateState, responseStatus: Dtos.ResponseStatus): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IProjectItemTemplateState, _isCreating: boolean): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IProjectItemTemplateState,
    formData: Dtos.ProjectItemTemplate,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IProjectItemTemplateState, responseStatus: Dtos.ResponseStatus): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IProjectItemTemplateState, _isValidating: boolean): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IProjectItemTemplateState,
    formData: Dtos.ProjectItemTemplate,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IProjectItemTemplateState, responseStatus: Dtos.ResponseStatus): IProjectItemTemplateState {
    const newState: IProjectItemTemplateState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const ProjectItemTemplateReducer: Reducer<IProjectItemTemplateState> = (state: IProjectItemTemplateState = initialState, action: ProjectItemTemplateAction) => {
    switch (action.type) {
        case ACTION_PROJECTITEMTEMPLATES_LOAD:
        case ACTION_PROJECTITEMTEMPLATES_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_PROJECTITEMTEMPLATES_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formDataGrouped, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_PROJECTITEMTEMPLATES_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_PROJECTITEMTEMPLATES_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_PROJECTITEMTEMPLATES_SAVE:
            return saving(state, true);
        case ACTION_PROJECTITEMTEMPLATES_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_PROJECTITEMTEMPLATES_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_PROJECTITEMTEMPLATES_CREATE:
            return creating(state, true);
        case ACTION_PROJECTITEMTEMPLATES_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_PROJECTITEMTEMPLATES_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_PROJECTITEMTEMPLATES_VALIDATE:
            return validating(state, true);
        case ACTION_PROJECTITEMTEMPLATES_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_PROJECTITEMTEMPLATES_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_PROJECTITEMTEMPLATES_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default ProjectItemTemplateReducer;